import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, 
  DialogDescription, DialogFooter 
} from '@/ui/dialog';
import { Button } from '@/ui/button';
import { Label } from '@/ui/label';
import { Input } from '@/ui/input';
import { Textarea } from '@/ui/textarea';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/ui/select';
import { toast } from 'sonner';
import API from '@/api/api01';
import { 
  User as UserIcon, Mail, Phone, BookOpen, 
  Activity, Tag, MessageSquare, Sparkles, 
  Save, X, ShieldCheck
} from 'lucide-react';
import { cn } from '@/ui/utils';

interface LeadEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: any;
  type: 'social' | 'general';
  onSuccess: () => void;
}

export default function LeadEditModal({ open, onOpenChange, lead, type, onSuccess }: LeadEditModalProps) {
  const [loading, setLoading] = useState(false);
  const savingRef = useRef(false);
  const [courses, setCourses] = useState<any[]>([]);
  const [counselors, setCounselors] = useState<any[]>([]);
  const [formData, setFormData] = useState<any>({});

  // Stable field handler — functional update prevents stale closure / input-skip glitch
  const handleField = useCallback((field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  }, []);

  useEffect(() => {
    if (open && lead) {
      setFormData({
        ...lead,
        // Normalize status field names
        status: lead.status || lead.lead_status || 'New',
        course: lead.course || '',
        counselor_name: lead.counselor_name || lead.counselor?.name || ''
      });
      fetchOptions();
    }
  }, [open, lead]);

  const fetchOptions = async () => {
    try {
      const [coursesRes, usersRes] = await Promise.all([
        API.get('/superadmin/courses'),
        API.get('/superadmin/users')
      ]);
      setCourses(coursesRes.data || []);
      setCounselors(usersRes.data || []);
    } catch (err) {
      console.error("Failed to load options", err);
    }
  };

  const handleSave = async () => {
    // Synchronous ref guard prevents double-save during React async state cycles
    if (savingRef.current) return;
    savingRef.current = true;
    setLoading(true);
    try {
      const endpoint = type === 'social'
        ? `/superadmin/social-leads/${lead.id}`
        : `/leads/${lead.id}`;

      const payload = { ...formData };

      // Clean up payload based on type
      if (type === 'general') {
        payload.lead_status = formData.status;
      }

      await API.put(endpoint, payload);
      toast.success("Lead updated successfully");
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      toast.error("Failed to update lead details");
    } finally {
      savingRef.current = false;
      setLoading(false);
    }
  };

  if (!lead) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] h-auto p-0 overflow-hidden bg-background/95 backdrop-blur-2xl border border-border shadow-[0_50px_100px_rgba(0,0,0,0.4)] rounded-3xl transition-all duration-500">
        <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600" />
        
        <DialogHeader className="p-8 pb-4">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-[10px] font-bold uppercase tracking-wider mb-2 border border-blue-500/20">
              <ShieldCheck size={12} />
              Lead Identity Management
            </div>
            <DialogTitle className="text-3xl font-black text-slate-900 dark:text-white tracking-tight leading-tight">
              Edit <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400">Lead Record</span>
            </DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-slate-400">
              Update prospect information with synchronized real-time validation.
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="p-8 pt-4 space-y-5 max-h-[60vh] overflow-y-auto scrollbar-hide">
          {/* Primary Contact Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-2 ml-1">
                <UserIcon size={14} className="text-blue-500" /> Full Name
              </Label>
               <Input
                value={formData.name || ''}
                onChange={e => handleField('name', e.target.value)}
                className="rounded-2xl h-12 bg-muted/50 border-border text-foreground focus:ring-blue-500/50 transition-all"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-2 ml-1">
                <Phone size={14} className="text-emerald-500" /> Phone Number
              </Label>
              <Input
                value={formData.phone || ''}
                onChange={e => handleField('phone', e.target.value)}
                className="rounded-2xl h-12 bg-muted/50 border-border text-foreground focus:ring-blue-500/50 transition-all font-mono"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-2 ml-1">
              <Mail size={14} className="text-purple-500" /> Email Address
            </Label>
            <Input
              value={formData.email || ''}
              onChange={e => handleField('email', e.target.value)}
              className="rounded-2xl h-12 bg-muted/50 border-border text-foreground focus:ring-blue-500/50 transition-all"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-2 ml-1">
                <BookOpen size={14} className="text-indigo-500" /> Industry/Course
              </Label>
              <Select
                value={formData.course || ''}
                onValueChange={v => handleField('course', v)}
              >
                <SelectTrigger className="rounded-2xl h-12 bg-muted/50 border-border text-foreground transition-all">
                  <SelectValue placeholder="Select course..." />
                </SelectTrigger>
                <SelectContent className="rounded-2xl border-border bg-popover text-foreground backdrop-blur-2xl">
                  {courses.map(c => (
                    <SelectItem key={c.id} value={c.name} className="rounded-xl py-3">
                      {c.name}
                    </SelectItem>
                  ))}
                   {/* Fallback for general leads manual entry */}
                   {!courses.some(c => c.name === formData.course) && formData.course && (
                     <SelectItem value={formData.course} className="rounded-xl py-3 italic">
                        {formData.course} (Custom)
                     </SelectItem>
                   )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-2 ml-1">
                <Tag size={14} className="text-amber-500" /> Lead Source
              </Label>
               <Select
                value={formData.source || lead.platform || ''}
                onValueChange={v => handleField('source', v)}
              >
                <SelectTrigger className="rounded-2xl h-12 bg-muted/50 border-border text-foreground transition-all">
                  <SelectValue placeholder="Select source..." />
                </SelectTrigger>
                <SelectContent className="rounded-2xl border-border bg-popover text-foreground backdrop-blur-2xl">
                  {['Website', 'Walk-in', 'Campaign', 'Referral', 'Instagram', 'Facebook', 'LinkedIn', 'YouTube'].map(s => (
                    <SelectItem key={s} value={s} className="rounded-xl py-2">
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-2 ml-1">
                <Activity size={14} className="text-orange-500" /> Score/Priority
              </Label>
              <Select
                value={String(formData.lead_score || formData.priority || '')}
                onValueChange={v => handleField('priority', v) || handleField('lead_score', isNaN(Number(v)) ? formData.lead_score : Number(v))}
              >
                <SelectTrigger className="rounded-2xl h-12 bg-muted/50 border-border text-foreground transition-all">
                  <SelectValue placeholder="Set priority..." />
                </SelectTrigger>
                <SelectContent className="rounded-2xl border-border bg-popover text-foreground backdrop-blur-2xl">
                  {type === 'social' ? (
                    [0, 20, 50, 80, 100].map(s => (
                      <SelectItem key={s} value={String(s)} className="rounded-xl py-2">
                        Score: {s}
                      </SelectItem>
                    ))
                  ) : (
                    ['Low', 'Medium', 'High'].map(p => (
                      <SelectItem key={p} value={p} className="rounded-xl py-2">
                        {p}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-2 ml-1">
                <Sparkles size={14} className="text-purple-500" /> Status
              </Label>
              <Select
                value={formData.status}
                onValueChange={v => handleField('status', v)}
              >
                <SelectTrigger className="rounded-2xl h-12 bg-muted/50 border-border text-foreground transition-all font-bold">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-2xl border-border bg-popover text-foreground backdrop-blur-2xl">
                  {['New', 'Verified', 'Contacted', 'Qualified', 'Converted', 'Lost'].map(s => (
                    <SelectItem key={s} value={s} className="rounded-xl py-2">
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-2 ml-1">
              <MessageSquare size={14} className="text-blue-400" /> Internal Notes
            </Label>
            <Textarea
              value={formData.notes || ''}
              onChange={e => handleField('notes', e.target.value)}
              placeholder="Maintain audit trail for this lead..."
              className="rounded-2xl bg-muted/50 border-border text-foreground focus:ring-blue-500/50 transition-all resize-none h-24 p-4 text-xs leading-relaxed"
            />
          </div>
        </div>

        <DialogFooter className="p-8 border-t border-border bg-muted/30 backdrop-blur-md flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            onMouseDown={(e) => e.preventDefault()}
            className="rounded-2xl h-12 px-8 font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 select-none"
          >
            <X size={18} className="mr-2" /> Discard
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            // CRITICAL: prevents mousedown from stealing focus from inputs
            // so the last typed character's onChange fires before save
            onMouseDown={(e) => e.preventDefault()}
            className={cn(
              "flex-1 rounded-2xl h-12 px-10 font-black shadow-xl transition-all duration-200 group overflow-hidden relative select-none",
              "bg-blue-600 hover:bg-blue-700 text-white min-w-[200px] disabled:opacity-70 disabled:cursor-not-allowed"
            )}
          >
            {loading ? (
              <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <div className="flex items-center justify-center gap-2">
                Commit Changes
                <Save size={18} className="group-hover:translate-y-[-1px] transition-transform" />
              </div>
            )}
            <div className="absolute top-0 -inset-full h-full w-1/2 z-5 block transform -skew-x-12 bg-gradient-to-r from-transparent to-white/10 opacity-40 group-hover:animate-shine" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
